import { isListingSchedulingOn, shouldShowListing } from "../util/listings";

export function applyFilters(listings) {
    listings.forEach((listingItem, i) => {
        if (listingItem.listings.length > 0) {
            listings[i] = {...listings[i], listings: applyFilters(listings[i].listings)}
        }
    });

    return listings.filter(listing => listingPassesColumnFilters(listing))
}

// A listing survives when it passes EVERY filtered column (AND across columns).
// Within a single column, (+) filters are alternatives (OR), and any (-) match
// removes the listing outright (exclusion wins). Matches bronco's
// getListingGroupListingsFD semantics.
export function listingPassesColumnFilters(listing) {
    for (let group of listing.filterGroup || []) {
        let colValue = String(listing.rawCols?.[group.colGroup] ?? '').toLowerCase()

        let addFilters = group.filters.filter(filter => filter.operation === '(+)')
        let removeFilters = group.filters.filter(filter => filter.operation === '(-)')

        if (removeFilters.some(filter => colValue.includes(filter.value.toLowerCase()))) {
            return false
        }

        if (addFilters.length > 0 && !addFilters.some(filter => colValue.includes(filter.value.toLowerCase()))) {
            return false
        }
    }

    return true
}

// Resolves the scheduling gate for ONE listing from its own owning listing_config
// (bronco nests a `listing_config.<uuid>` key under every `listing.<uuid>` node in
// flat data). A collection can hold listings from several nested configs, and the
// collection-tier config is usually the parent/category tier whose toggle is unset,
// so the gate must be resolved per listing, never once per collection (DEV-12036).
// `toggleCache` memoizes per config UUID because isListingSchedulingOn scans flatData.
function isSchedulingOnForListing(listingItem, fallbackGate, toggleCache) {
    const flatData = window.flatData || {}
    const ownConfigUUID = Object.keys(flatData[listingItem.m1Key] || {})
        .find((m2Key) => m2Key.startsWith('listing_config.'))

    if (!ownConfigUUID) {
        return fallbackGate
    }

    if (!toggleCache.has(ownConfigUUID)) {
        toggleCache.set(ownConfigUUID, isListingSchedulingOn(flatData, ownConfigUUID))
    }

    return toggleCache.get(ownConfigUUID)
}

// `fallbackGate` only applies to listings whose flat-data node carries no
// listing_config.* key; every other listing is gated by its own config's toggle.
export function buildFilterList(listing, fallbackGate = true, toggleCache = new Map()) {
    const retval = listing.filter((listingItem) => {
        if (!isSchedulingOnForListing(listingItem, fallbackGate, toggleCache)) {
            return true
        }

        return shouldShowListing(window.flatData?.[listingItem.m1Key]?.listing_scheduling)
    });

    retval
        .forEach((listingItem, i) => {
            if (listingItem.listings.length > 0) {
                retval[i] = {...retval[i], listings: buildFilterList(retval[i].listings, fallbackGate, toggleCache)}
            }

            let filterGroup = []
            let filterOperations = {add: false, remove: false}

            listingItem.colGroups.forEach(colGroup => {
                if (colGroup.filter) {
                    filterGroup.push({filters: formatFilters(colGroup.filter), colGroup: colGroup.m1Key})
                }
            });

            filterGroup.forEach(group => {
                group.filters.forEach(filters => {
                    if (filters.operation === '(+)') {
                        filterOperations.add = true
                    }
                    if (filters.operation === '(-)') {
                        filterOperations.remove = true
                    }
                });
            });
            
            retval[i] = {
                ...retval[i], 
                filterGroup, 
                filterOperations 
            }
        });

    return retval
}

export function formatFilters(filters) {
    if (filters.length === 0) {
        return []
    }

    let finalArrayOfFilters = []
    
    filters.forEach(filter => {
        finalArrayOfFilters.push({
            operation: filter.slice(0,3),
            value: filter.slice(3) 
        })
    }); 

    return finalArrayOfFilters
}